import * as THREE from 'three';

/**
 * 草图参数曲线最近点求解器。
 *
 * 职责：
 * 1. 在有限参数区间内先做全局粗搜索，避免只依赖鼠标附近显示弦造成错误分支；
 * 2. 对最优粗采样区间执行黄金分割细化；
 * 3. 最终结果必须由权威参数曲线求值器生成，不能返回渲染折线插值点。
 */
export class SketchParametricCurveNearestPointSolver {
    /** 默认全局粗搜索区段数。 */
    private static readonly DEFAULT_SAMPLE_COUNT = 96;

    /** 黄金分割比例。 */
    private static readonly GOLDEN_RATIO = (Math.sqrt(5) - 1) / 2;

    /** 参数细化最大迭代次数。 */
    private static readonly MAX_REFINEMENT_ITERATIONS = 48;

    /** 参数收敛相对容差。 */
    private static readonly PARAMETER_TOLERANCE = 1e-12;

    /** 复用的曲线求值点。 */
    private readonly evaluationPoint = new THREE.Vector3();

    /** 复用的左侧黄金分割点。 */
    private readonly leftGoldenPoint = new THREE.Vector3();

    /** 复用的右侧黄金分割点。 */
    private readonly rightGoldenPoint = new THREE.Vector3();

    /** 复用的候选结果点。 */
    private readonly candidatePoint = new THREE.Vector3();

    /** 复用的最终结果点。 */
    private readonly resultPoint = new THREE.Vector3();

    /**
     * 求解有限参数曲线到指定世界坐标的最近点。
     *
     * @param worldPosition 待投影世界坐标。
     * @param startParameter 有效参数起点。
     * @param endParameter 有效参数终点。
     * @param evaluatePoint 权威曲线求值器；必须把结果写入 target 并返回 target。
     * @param sampleCount 全局粗搜索区段数。
     * @param closedCurve 参数区间首尾是否表达同一闭合点。
     * @returns 权威曲线上的最近世界坐标；参数域无效时返回 null。
     */
    public solve(
        worldPosition: THREE.Vector3,
        startParameter: number,
        endParameter: number,
        evaluatePoint: (parameter: number, target: THREE.Vector3) => THREE.Vector3,
        sampleCount: number = SketchParametricCurveNearestPointSolver.DEFAULT_SAMPLE_COUNT,
        closedCurve: boolean = false,
    ): THREE.Vector3 | null {
        if (!Number.isFinite(startParameter) || !Number.isFinite(endParameter)) {
            return null;
        }

        const parameterSpan = endParameter - startParameter;
        if (Math.abs(parameterSpan) <= Number.EPSILON) {
            return evaluatePoint(startParameter, this.resultPoint).clone();
        }

        const resolvedSampleCount = Math.max(8, Math.floor(sampleCount));
        let bestSampleIndex = 0;
        let bestDistanceSquared = Number.POSITIVE_INFINITY;

        // 全参数域粗搜索用于定位全局最近分支，显示折线只负责鼠标悬停，不参与最终几何计算。
        const sampleEndIndex = closedCurve ? resolvedSampleCount - 1 : resolvedSampleCount;
        for (let index = 0; index <= sampleEndIndex; index += 1) {
            const parameter = startParameter + parameterSpan * (index / resolvedSampleCount);
            this.evaluateWithinDomain(
                parameter,
                startParameter,
                endParameter,
                closedCurve,
                evaluatePoint,
                this.evaluationPoint,
            );
            const distanceSquared = worldPosition.distanceToSquared(this.evaluationPoint);
            if (distanceSquared < bestDistanceSquared) {
                bestDistanceSquared = distanceSquared;
                bestSampleIndex = index;
                this.resultPoint.copy(this.evaluationPoint);
            }
        }

        const leftIndex = closedCurve ? bestSampleIndex - 1 : Math.max(0, bestSampleIndex - 1);
        const rightIndex = closedCurve
            ? bestSampleIndex + 1
            : Math.min(resolvedSampleCount, bestSampleIndex + 1);
        let leftParameter = startParameter + parameterSpan * (leftIndex / resolvedSampleCount);
        let rightParameter = startParameter + parameterSpan * (rightIndex / resolvedSampleCount);

        // 允许调用方使用反向参数区间；细化算法内部统一保持左值小于右值。
        if (leftParameter > rightParameter) {
            const swappedParameter = leftParameter;
            leftParameter = rightParameter;
            rightParameter = swappedParameter;
        }

        const goldenRatio = SketchParametricCurveNearestPointSolver.GOLDEN_RATIO;
        let leftGoldenParameter = rightParameter - goldenRatio * (rightParameter - leftParameter);
        let rightGoldenParameter = leftParameter + goldenRatio * (rightParameter - leftParameter);
        this.evaluateWithinDomain(
            leftGoldenParameter,
            startParameter,
            endParameter,
            closedCurve,
            evaluatePoint,
            this.leftGoldenPoint,
        );
        this.evaluateWithinDomain(
            rightGoldenParameter,
            startParameter,
            endParameter,
            closedCurve,
            evaluatePoint,
            this.rightGoldenPoint,
        );
        let leftDistanceSquared = worldPosition.distanceToSquared(this.leftGoldenPoint);
        let rightDistanceSquared = worldPosition.distanceToSquared(this.rightGoldenPoint);

        for (
            let iteration = 0;
            iteration < SketchParametricCurveNearestPointSolver.MAX_REFINEMENT_ITERATIONS;
            iteration += 1
        ) {
            const tolerance = SketchParametricCurveNearestPointSolver.PARAMETER_TOLERANCE
                * Math.max(1, Math.abs(leftParameter), Math.abs(rightParameter));
            if (rightParameter - leftParameter <= tolerance) {
                break;
            }

            if (leftDistanceSquared <= rightDistanceSquared) {
                rightParameter = rightGoldenParameter;
                rightGoldenParameter = leftGoldenParameter;
                this.rightGoldenPoint.copy(this.leftGoldenPoint);
                rightDistanceSquared = leftDistanceSquared;
                leftGoldenParameter = rightParameter - goldenRatio * (rightParameter - leftParameter);
                this.evaluateWithinDomain(
                    leftGoldenParameter,
                    startParameter,
                    endParameter,
                    closedCurve,
                    evaluatePoint,
                    this.leftGoldenPoint,
                );
                leftDistanceSquared = worldPosition.distanceToSquared(this.leftGoldenPoint);
            } else {
                leftParameter = leftGoldenParameter;
                leftGoldenParameter = rightGoldenParameter;
                this.leftGoldenPoint.copy(this.rightGoldenPoint);
                leftDistanceSquared = rightDistanceSquared;
                rightGoldenParameter = leftParameter + goldenRatio * (rightParameter - leftParameter);
                this.evaluateWithinDomain(
                    rightGoldenParameter,
                    startParameter,
                    endParameter,
                    closedCurve,
                    evaluatePoint,
                    this.rightGoldenPoint,
                );
                rightDistanceSquared = worldPosition.distanceToSquared(this.rightGoldenPoint);
            }
        }

        const refinedParameter = leftDistanceSquared <= rightDistanceSquared
            ? leftGoldenParameter
            : rightGoldenParameter;
        this.evaluateWithinDomain(
            refinedParameter,
            startParameter,
            endParameter,
            closedCurve,
            evaluatePoint,
            this.candidatePoint,
        );
        const refinedDistanceSquared = worldPosition.distanceToSquared(this.candidatePoint);
        if (refinedDistanceSquared < bestDistanceSquared) {
            this.resultPoint.copy(this.candidatePoint);
        }

        // 返回独立副本，避免下一帧求解覆盖已提交的捕捉坐标。
        return this.resultPoint.clone();
    }

    /**
     * 在有限或闭合参数域内执行一次权威曲线求值。
     * @param parameter 原始参数。
     * @param startParameter 参数起点。
     * @param endParameter 参数终点。
     * @param closedCurve 参数域是否闭合。
     * @param evaluatePoint 权威曲线求值器。
     * @param target 结果写入目标。
     * @returns 写入后的目标。
     */
    private evaluateWithinDomain(
        parameter: number,
        startParameter: number,
        endParameter: number,
        closedCurve: boolean,
        evaluatePoint: (parameter: number, target: THREE.Vector3) => THREE.Vector3,
        target: THREE.Vector3,
    ): THREE.Vector3 {
        if (!closedCurve) {
            return evaluatePoint(parameter, target);
        }
        const parameterSpan = endParameter - startParameter;
        const normalizedRatio = (
            ((parameter - startParameter) / parameterSpan) % 1 + 1
        ) % 1;
        return evaluatePoint(startParameter + normalizedRatio * parameterSpan, target);
    }
}
